import { clock } from "@faire/web/common/clock";
import differenceInDays from "date-fns/differenceInDays";
import differenceInHours from "date-fns/differenceInHours";
import { useEffect, useMemo, useState, type JSX } from "react";

import { setIntervalIfPossible } from "@faire/retailer/lib/async-functions/setIntervalIfPossible";
import { useIsLoggedInRetailer } from "@faire/retailer/lib/isLoggedInRetailer";
import { useMarketStore } from "@faire/retailer/stores/domain/Events/FaireMarkets/MarketStore";

import { countdownString } from "../utils";

export interface IProps {
  /** Client should ensure that the given deadline is after the current date. */
  deadline: number | Date;
  daysBeforeDeadlineView: (
    daysLeft: number,
    mainEventRangeDate: string
  ) => JSX.Element;
  hoursBeforeDeadlineView: (timeLeft: string, hoursLeft: number) => JSX.Element;
  loggedOutView?: (lastDay: boolean, mainEventRangeDate: string) => JSX.Element;
  onCountdownChange: () => void;
}

export const CountdownBannerBase = ({
  deadline,
  daysBeforeDeadlineView,
  hoursBeforeDeadlineView,
  loggedOutView,
  onCountdownChange,
}: IProps) => {
  const [currentDate, setCurrentDate] = useState(() => clock.date());
  const isLoggedInRetailer = useIsLoggedInRetailer();
  const deadlineDate = useMemo(() => new Date(deadline), [deadline]);
  const { latestMarketMainEventRangeDate } = useMarketStore([
    "latestMarketMainEventRangeDate",
  ]);
  const daysBeforeDeadline = useMemo(
    () => differenceInDays(deadlineDate, currentDate),
    [currentDate, deadlineDate]
  );
  const showHourCountdown = daysBeforeDeadline <= 0;

  useEffect(
    () => onCountdownChange(),
    [currentDate, deadlineDate, onCountdownChange]
  );

  useEffect(() => {
    const interval = setIntervalIfPossible(
      () => setCurrentDate(clock.date()),
      1000
    );

    return () => clearInterval(interval);
  }, []);

  if (!isLoggedInRetailer && loggedOutView) {
    return loggedOutView(showHourCountdown, latestMarketMainEventRangeDate);
  }

  if (showHourCountdown) {
    return hoursBeforeDeadlineView(
      countdownString(deadlineDate, currentDate),
      differenceInHours(deadlineDate, currentDate) + 1
    );
  }

  return daysBeforeDeadlineView(
    daysBeforeDeadline,
    latestMarketMainEventRangeDate
  );
};
